/**
 * CascadeDeleteModal — confirmation modal for opt-in cascade deletes.
 *
 * Renders the list of attachments that will be soft-deleted alongside
 * the parent entity (Person / Account / Agreement / InsurancePolicy),
 * grouped by table with friendly row-level labels (e.g.
 * "CIIF-011 — HOLDER"). Requires the user to tick an acknowledgement
 * checkbox before the Cascade delete button is enabled, so the action
 * cannot be triggered by an accidental Enter or mis-click.
 *
 * Charter §5: human-in-the-loop. The modal IS the human checkpoint.
 *
 * Used by Beta #11 cascade-delete flow. See
 * _MEMORY/decisions/2026-05-26_cascade-delete-design.md.
 */
import {
  Alert, Button, Checkbox, Group, Modal, Stack, Text, Title,
} from '@mantine/core';
import { IconAlertTriangle, IconTrash } from '@tabler/icons-react';
import { useState, type ReactNode } from 'react';

import type { ReferencesReport } from '../api/persons';

export interface CascadeDeleteModalProps {
  opened: boolean;
  onClose: () => void;
  /** What's being deleted — used for the modal title + checkbox copy. */
  entityNoun: string;
  /** Optional friendly identifier of the entity for the title.
   * E.g. "Mariana Cordovil" → title becomes
   * "Cascade delete this Person? (Mariana Cordovil)". */
  entityLabel?: string;
  /** The references report from GET /<entity>/{id}/references. */
  report: ReferencesReport | undefined;
  /** Called with the ref_ids payload when the user confirms the cascade. */
  onConfirm: (refIds: Record<string, string[]>) => void;
  /** True while the cascade mutation is in flight. */
  loading?: boolean;
}

// Human-readable section labels per backend table name.
const SECTION_LABELS: Record<string, string> = {
  agreement_parties:        'Agreement attachments',
  account_parties:          'Account attachments',
  insurance_policy_parties: 'Insurance policy attachments',
  insurance_policy_accounts:'Policy ↔ Account links',
  agreement_documents:      'Agreement documents (IAA history)',
  accounts:                 'Child accounts',
};


export function CascadeDeleteModal({
  opened, onClose, entityNoun, entityLabel,
  report, onConfirm, loading,
}: CascadeDeleteModalProps) {
  const [confirmed, setConfirmed] = useState(false);

  if (!report) {
    return (
      <Modal opened={opened} onClose={onClose} title={`Cascade delete this ${entityNoun}?`} centered>
        <Text c="dimmed" size="sm">Loading attachment list…</Text>
      </Modal>
    );
  }

  const sections: ReactNode[] = [];
  for (const [table, rows] of Object.entries(report.references)) {
    if (!rows || rows.length === 0) continue;
    const heading = SECTION_LABELS[table] ?? table;
    sections.push(
      <Stack key={table} gap={2}>
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
          {heading} · {rows.length}
        </Text>
        <Stack gap={2} pl="md">
          {rows.map((r) => (
            <Text key={r.id} size="sm">• {r.label}</Text>
          ))}
        </Stack>
      </Stack>,
    );
  }

  const totalRefs = Object.values(report.references)
    .reduce((acc, rows) => acc + (rows?.length ?? 0), 0);

  const handleConfirm = () => {
    if (!confirmed || !report) return;
    // Build the ref_ids payload (table → list of UUID strings).
    const payload: Record<string, string[]> = {};
    for (const [table, rows] of Object.entries(report.references)) {
      if (rows && rows.length > 0) payload[table] = rows.map((r) => r.id);
    }
    onConfirm(payload);
  };

  const title = entityLabel
    ? `Cascade delete this ${entityNoun}? (${entityLabel})`
    : `Cascade delete this ${entityNoun}?`;

  return (
    <Modal
      opened={opened}
      onClose={() => { setConfirmed(false); onClose(); }}
      title={<Title order={4}>{title}</Title>}
      centered
      size="lg"
      // Don't allow closing while a mutation is in flight.
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
      withCloseButton={!loading}
    >
      <Stack gap="md">
        <Alert
          icon={<IconAlertTriangle size={20} />}
          color="orange"
          variant="light"
        >
          This action will soft-delete the {entityNoun} <strong>and {totalRefs} attached
          record(s)</strong>. All affected rows share the same change-request
          ID in the audit log. Soft-deletes are reversible per row via the
          admin Restore flow, but a single Restore on the {entityNoun} alone
          does NOT auto-restore the attachments.
        </Alert>

        <Stack gap="sm">{sections}</Stack>

        <Checkbox
          checked={confirmed}
          onChange={(e) => setConfirmed(e.currentTarget.checked)}
          label={
            <Text size="sm">
              I understand. Delete the {entityNoun.toLowerCase()} and all
              listed attachments.
            </Text>
          }
        />

        <Group justify="flex-end" gap="sm" mt="sm">
          <Button
            variant="default"
            onClick={() => { setConfirmed(false); onClose(); }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            color="red"
            leftSection={<IconTrash size={16} />}
            onClick={handleConfirm}
            disabled={!confirmed || loading}
            loading={loading}
          >
            Cascade delete
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
